import { useEffect, useContext, useMemo, useState } from 'react';
import { format, sub } from 'date-fns';
import { useRemote } from '../../../Utils/Utils';
import { getMetric, analyticsExportFilename } from '../AnalyticsUtils';
import { BannerContext } from '../../../InternalApiApp';
import AnalyticsHeader from './AnalyticsHeader';
import Divider from '../../../components/Divider';
import LoadingIcon from '../../Shared/LoadingIcon';

const TimePeriodComparison = ({ children, label, mb, customeDate }) => {
    const remote = useRemote();
    const end = format(new Date(), 'yyyy-MM-dd');
    const [data, setData] = useState([]);
    const { bannerAdditionalInfo } = useContext(BannerContext)

    useEffect(() => {

        const products = bannerAdditionalInfo?.platforms.map(platform =>
            platform.products.map(p => p.selected ? `product=${p.id}` : null)
                .filter(e => e !== null)
                .join("&")
        )

        const productsParam = products ? `&${products}` : '';

        if (children && children.length > 0) {
            Promise.all(
                children.map((child) => {
                    const { endpoint, metrics, timeFrame } = child;
                    let time = timeFrame
                    let time2 = time.years ? { years: time.years * 2 } : { months: time.months * 2 }
                    const date1 = format(sub(new Date(), time), "yyyy-MM-dd")
                    const date2 = format(sub(new Date(), time2), "yyyy-MM-dd")
                    const end2 = format(sub(new Date(), time), "yyyy-MM-dd")

                    const endpoint1 = endpoint.replace('${date}', date1).replace('${end}', end).replace('${products}', productsParam);
                    const endpoint2 = endpoint.replace('${date}', date2).replace('${end}', end2).replace('${products}', productsParam);

                    const promise1 = remote.get(endpoint1)
                    const promise2 = remote.get(endpoint2)

                    return Promise.all([promise1, promise2]).then(([currentPeriodData, previousPeriodData]) => {
                        const data1 = Array.isArray(currentPeriodData) ? currentPeriodData : [{ [metrics.method ? metrics.column : 'value']: currentPeriodData }];
                        const formattedData1 = metrics.method ? getMetric(data1, { ...metrics, label: `Current ${metrics.label}` }) : { ...data1[0], label: `Current ${metrics.label}` };
                        const data2 = Array.isArray(previousPeriodData) ? previousPeriodData : [{ [metrics.method ? metrics.column : 'value']: previousPeriodData }];
                        const formattedData2 = metrics.method ? getMetric(data2, { ...metrics, label: `Last ${metrics.label}` }) : { ...data2[0], label: `Last ${metrics.label}` };

                        return { current: formattedData1, previous: formattedData2 }
                    });

                })).then((resp) => {
                    setData(resp);
                })
        }

    }, [bannerAdditionalInfo, end, remote, children]);


    const exportHeaders = useMemo(() => [
        { label: 'Period', key: 'period' },
        { label: 'Value', key: 'value' },
    ], []);

    const exportData = useMemo(() => {
        if (!Array.isArray(data)) return [];
        const rows = [];
        data.forEach(({ current, previous }) => {
            if (current) {
                rows.push({ period: current.label, value: current.value });
            }
            if (previous) {
                rows.push({ period: previous.label, value: previous.value });
            }
        });
        return rows;
    }, [data]);

    const exportFilename = useMemo(
        () => analyticsExportFilename('pages-per-session', customeDate),
        [customeDate]
    );

    return (
        <>
            <AnalyticsHeader
                mb={mb ? mb :'m'}
                exportHeaders={exportHeaders}
                exportData={exportData}
                exportFilename={exportFilename}
            >{label ? label : "Search Terms"}</AnalyticsHeader>
            <div style={{ display: 'flex', gap: '1rem' }} className='mt-s'>
                {data && data.length > 0 ? data.map(({ current, previous }, index) => {
                    return (
                        <>
                            {index > 0 && <Divider vertical height={60} />}
                            <div style={{ display: 'flex', gap: '1rem' }} className={`${mb ? `mb-${mb}` : ''}`}>
                                <div style={{
                                    display: 'flex', flexDirection: 'column', gap: '0.25rem',
                                    color: current?.value < previous?.value ? "#E66780" : "#56D1C3", padding: "0.5rem", borderRadius: "0.25rem"
                                }}>
                                    <div style={{ color: "#333B5A" }} className='font-size-body-xs'>{current?.label}</div>
                                    <div className='font-size-header-3 font-weight-medium'>{current?.value.toFixed(1)}</div>
                                </div>

                                <div style={{
                                    display: 'flex', flexDirection: 'column', gap: '0.25rem',
                                    color: "#ADB1BD", padding: "0.5rem", borderRadius: "0.25rem"
                                }}>
                                    <div style={{ color: "#333B5A" }} className='font-size-body-xs'>{previous?.label}</div>
                                    <div style={{ color: "#333B5A" }} className='font-size-header-3 font-weight-medium'>{previous?.value.toFixed(1)}</div>
                                </div>
                            </div>
                        </>
                    )
                })
                    : <LoadingIcon centered/>
                }
            </div>
        </>
    );
}

export default TimePeriodComparison;